'use client';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { faqService, FAQItem as FAQServiceItem } from '../services/faqService';
import { trackDiscoveryCallClick } from '@/lib/analytics';
import { getUserContext } from '@/lib/userContext';
import { openCalendly } from '@/lib/ctaRouting';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';


interface FAQItem {
  id: string;
  question: string;
  answer: string;
  isExpanded: boolean;
}

export default function FAQSection() {
  const router = useRouter();
  const { isAuthenticated, user } = useAuth();
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async (): Promise<void> => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await faqService.getAll();
      
      if (fetchError) {
        throw fetchError;
      }

      // Transform service items to component state format
      const transformedItems: FAQItem[] = (data || []).map((item: FAQServiceItem) => ({
        id: item.id,
        question: item.question,
        answer: item.answer,
        isExpanded: false
      }));

      setFaqItems(transformedItems);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load FAQs';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (id: string): void => {
    setFaqItems(items =>
      items.map(item =>
        item.id === id ? { ...item, isExpanded: !item.isExpanded } : { ...item, isExpanded: false }
      )
    );
  };

  const handleTalkToTeam = () => {
    getUserContext().then(context => {
      const segment = context?.role || 'unauthenticated';
      trackDiscoveryCallClick(
        segment === 'super_admin' ? 'super_admin' : 
        segment === 'ops_admin' || segment === 'admin' ? 'admin' : 
        segment === 'team' ? 'team' : 
        segment === 'client' ? 'client' : 'unauthenticated',
        '/',
        'faq_top'
      );
    });
    openCalendly();
  };

  const featureCards = [
    {
      icon: "/images/img_search.svg",
      title: "Unlimited Requests",
      description: "Queue up as many tasks as you need"
    },
    {
      icon: "/images/img_icon_purple_600_40x40.svg",
      title: "Fast Turnaround",
      description: "Average 24-72 hour delivery"
    },
    {
      icon: "/images/img_icon_40x40.svg",
      title: "Expert Team",
      description: "Designers, editors, and strategists"
    },
    {
      icon: "/images/img_icon_7.svg",
      title: "Unlimited Revisions",
      description: "Perfect until you\'re satisfied"
    }
  ];

  const createCheckoutSessionWithTimeout = async (planId: string, attempt: number = 1): Promise<{ success: boolean; sessionUrl?: string; error?: string }> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId }),
        credentials: 'include',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const checkoutData = await response.json();

      if (response.ok && checkoutData.sessionUrl) {
        return { success: true, sessionUrl: checkoutData.sessionUrl };
      } else {
        return { success: false, error: checkoutData.error || 'Failed to create checkout session' };
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      
      if (err.name === 'AbortError') {
        return { success: false, error: 'Request timed out. Please check your connection and try again.' };
      }
      
      if (!navigator.onLine) {
        return { success: false, error: 'No internet connection. Please check your network and try again.' };
      }
      
      return { success: false, error: 'Network error occurred. Please try again.' };
    }
  };

  const handleGetStarted = async (planName: string) => {
    try {
      // Map plan names to plan IDs
      const planIdMap: Record<string, string> = {
        'Sirloin Cut': 'sirloin',
        'Ribeye Cut': 'ribeye',
        'Tomahawk Cut': 'tomahawk'
      };
      const planId = planIdMap[planName];

      if (!planId) {
        console.error('Invalid plan name:', planName);
        setCheckoutError('Invalid plan selected. Please try again.');
        return;
      }

      console.log('handleGetStarted called:', { planName, planId, isAuthenticated, hasUser: !!user });

      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        // User is NOT authenticated - redirect to login with plan parameter
        console.log('User not authenticated, redirecting to login with plan:', planName);
        const loginUrl = `/login?plan=${encodeURIComponent(planName)}`;
        console.log('Redirecting to:', loginUrl);
        
        // Use window.location for more reliable redirect
        window.location.href = loginUrl;
        return;
      }

      // User IS authenticated - create checkout session directly with retry logic
      console.log('User authenticated, creating checkout session');
      setCheckoutLoading(planName);
      setCheckoutError(null);
      setRetryCount(0);
      
      let attempt = 1;
      
      while (attempt <= MAX_RETRIES) {
        const result = await createCheckoutSessionWithTimeout(planId, attempt);
        
        if (result.success && result.sessionUrl) {
          // Redirect to Stripe checkout
          console.log('Checkout session created, redirecting to:', result.sessionUrl);
          window.location.href = result.sessionUrl;
          return;
        }
        
        // If failed and we have retries left
        if (attempt < MAX_RETRIES) {
          setRetryCount(attempt);
          setCheckoutError(`Checkout failed (attempt ${attempt}/${MAX_RETRIES}). Retrying...`);
          await new Promise(resolve => setTimeout(resolve, 1500)); // Wait 1.5s before retry
          attempt++;
        } else {
          // All retries exhausted
          console.error('Checkout session creation failed after retries:', result.error);
          setCheckoutError(result.error || 'Failed to create checkout session after multiple attempts. Please try again later.');
          setCheckoutLoading(null);
          
          // Show error toast/message for 5 seconds
          setTimeout(() => {
            setCheckoutError(null);
          }, 5000);
          return;
        }
      }
    } catch (error) {
      console.error('Error in handleGetStarted:', error);
      setCheckoutError('An unexpected error occurred. Please try again.');
      setCheckoutLoading(null);
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setCheckoutError(null);
      }, 5000);
    }
  };

  return (
    <section className="w-full">
      {/* FAQ Section - Dark Purple Background */}
      <div className="w-full bg-[#2a0f3a] py-12 sm:py-16 lg:py-20 relative overflow-hidden">
        <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 xl:px-36">
          
          {/* Top Row: Title + Feature Cards */}
          <div className="flex flex-col lg:flex-row justify-between items-start w-full gap-8 lg:gap-12 mb-10 lg:mb-14">
            
            {/* Left: Title, Description, Button */}
            <div className="flex flex-col gap-4 sm:gap-6 w-full lg:w-[50%]">
              <div className="flex flex-col">
                <h2 className="text-[40px] sm:text-[55px] lg:text-[70px] xl:text-[80px] font-bebas font-normal leading-[0.95] text-left text-[#ffefef] uppercase">
                  Got Questions?
                </h2>
                <h3 className="text-[40px] sm:text-[55px] lg:text-[70px] xl:text-[80px] font-bebas font-normal leading-[0.95] text-left text-[#ffefef] uppercase">
                  We've Got Answers
                </h3>
              </div>

              <p className="text-[14px] sm:text-[15px] lg:text-[16px] font-poppins font-normal leading-[1.6] text-left text-[#e4e4e4] max-w-md">
                Here are the most common questions we get about Purple Cow Unlimited. Still have questions? Book a call with us.
              </p>

              <a
                href="https://calendly.com/john-smulo/purple-cow-unlimited-discovery-call"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleTalkToTeam}
              >
                <Button
                  text="TALK TO OUR TEAM"
                  text_font_size="text-xs sm:text-sm"
                  text_font_family="Helvetica"
                  text_font_weight="font-medium"
                  text_line_height="leading-normal"
                  text_text_align="center"
                  text_text_transform="uppercase"
                  text_color="text-white"
                  fill_background_color="bg-transparent"
                  border_border_radius="rounded-full"
                  className="w-full sm:w-auto py-4 px-8 border-2 border-white hover:bg-white hover:text-[#3f1651] transition-all duration-300 tracking-wider"
                />
              </a>
            </div>

            {/* Right: Feature Cards 2x2 Grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-5 w-full lg:w-[45%]">
              {featureCards.map((card, index) => (
                <div 
                  key={index}
                  className="flex flex-col gap-2 sm:gap-3 justify-start items-center w-full border border-[#5a3d6a] rounded-lg bg-[#3f1651]/60 p-4 sm:p-5 lg:p-6"
                >
                  <Image 
                    src={card.icon} 
                    alt={`${card.title} icon`} 
                    width={40} 
                    height={40} 
                    className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10" 
                  />
                  
                  <h5 className="text-[13px] sm:text-[15px] lg:text-[17px] font-bebas font-normal leading-[1.2] text-center text-white uppercase tracking-wide">
                    {card.title}
                  </h5>
                  
                  <p className="text-[11px] sm:text-[12px] lg:text-[13px] font-poppins font-normal leading-[1.5] text-center text-[#c4c4c4]">
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Row: FAQ Accordion + Cow Mascot */}
          <div className="flex flex-col lg:flex-row justify-between items-start w-full gap-8 lg:gap-12 relative">
            
            {/* Left: FAQ Items */}
            <div className="flex flex-col gap-3 sm:gap-4 w-full lg:w-[50%] relative z-10">
              {loading && (
                <div className="flex justify-center items-center py-8">
                  <p className="text-white text-lg">Loading FAQs...</p>
                </div>
              )}

              {error && (
                <div className="flex justify-center items-center py-8">
                  <p className="text-red-400 text-lg">{error}</p>
                </div>
              )}

              {!loading && !error && faqItems?.length === 0 && (
                <div className="flex justify-center items-center py-8">
                  <p className="text-white text-lg">No FAQs available</p>
                </div>
              )}

              {!loading && !error && faqItems?.map((item) => (
                <div key={item?.id} className="w-full">
                  <button
                    className="flex justify-between items-center w-full border border-[#6b4f7a] rounded-lg bg-[#3f1651]/40 p-4 sm:p-5 lg:p-6 cursor-pointer hover:bg-[#3f1651]/60 transition-all duration-200 text-left"
                    onClick={() => toggleFAQ(item?.id)}
                    aria-expanded={item?.isExpanded}
                    aria-controls={`faq-answer-${item?.id}`}
                  >
                    <h4 className="text-[14px] sm:text-[16px] lg:text-[20px] font-bebas font-normal leading-[1.2] text-left text-white flex-1 pr-3 sm:pr-4 uppercase tracking-wide">
                      {item?.question}
                    </h4>
                    
                    <svg 
                      className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-white/70 transition-transform duration-300 ease-in-out ${item?.isExpanded ? 'rotate-180' : 'rotate-0'}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Expandable answer area with smooth animation */}
                  <div 
                    id={`faq-answer-${item?.id}`}
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      item?.isExpanded ? 'max-h-[500px] opacity-100 mt-2' : 'max-h-0 opacity-0'
                    }`}
                    role="region"
                    aria-labelledby={`faq-question-${item?.id}`}
                  >
                    <div className="p-4 sm:p-5 lg:p-6 bg-[#3f1651]/20 rounded-lg border border-[#6b4f7a]/50">
                      <p className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.6] text-[#e4e4e4]">
                        {item?.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Empty space for cow mascot */}
            <div className="hidden lg:block lg:w-[45%]"></div>
          </div>
        </div>

        {/* Cow Mascot - Positioned to overlap both sections */}
        <div className="absolute right-[5%] lg:right-[10%] xl:right-[15%] bottom-[-250px] sm:bottom-[-300px] lg:bottom-[-350px] z-30 hidden sm:block pointer-events-none">
          <Image 
            src="/images/img_group_1258_2x_1_png.png" 
            alt="Purple cow mascot" 
            width={450} 
            height={900}
            className="w-[250px] sm:w-[320px] lg:w-[400px] xl:w-[450px] h-auto object-contain"
          />
        </div>
      </div>

      {/* Choose Your Plan Section */}
      <div 
        className="w-full pt-20 sm:pt-24 lg:pt-28 pb-16 sm:pb-20 lg:pb-28 relative overflow-hidden"
        style={{
          backgroundImage: "url('/faq_bg.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 xl:px-36 relative z-10">
          <div className="flex flex-col justify-center items-center w-full">
            
            {/* Ready to Start Badge */}
            <div className="flex gap-2 justify-center items-center bg-white rounded-full px-4 py-2 mb-6 sm:mb-8">
              <Image src="/images/img_icon_16x16.svg" alt="Ready icon" width={16} height={16} className="w-4 h-4" />
              <span className="text-[11px] sm:text-xs font-helvetica font-medium leading-normal text-[#8923b3] uppercase tracking-wider">Ready to Start?</span>
            </div>

            {/* Title */}
            <h2 className="text-[40px] sm:text-[60px] lg:text-[80px] xl:text-[100px] font-bebas font-normal leading-[0.95] text-center text-white uppercase">
              Choose Your Plan
            </h2>
            <h3 className="text-[40px] sm:text-[60px] lg:text-[80px] xl:text-[100px] font-bebas font-normal leading-[0.95] text-center text-white uppercase">
              And Start Now
            </h3>

            {/* Pricing Cards */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 lg:gap-6 mt-10 sm:mt-12 lg:mt-14 w-full justify-center items-center">
              
              {/* Sirloin Cut */}
              <div className="flex flex-col gap-4 justify-center items-center bg-white rounded-2xl p-6 sm:p-7 lg:p-8 shadow-xl w-full sm:w-[220px] lg:w-[260px]">
                <h4 className="text-[22px] sm:text-[26px] lg:text-[30px] font-bebas font-normal leading-[1.2] text-center text-[#3f1651] uppercase">
                  Sirloin Cut
                </h4>
                <span className="text-[32px] sm:text-[38px] lg:text-[44px] font-bebas font-normal leading-[1.1] text-center text-[#8923b3]">
                  $1,600
                </span>
                <button
                  onClick={() => handleGetStarted('Sirloin Cut')}
                  disabled={checkoutLoading === 'Sirloin Cut'}
                  className="w-full py-3.5 px-6 bg-[#8923b3] text-white text-xs sm:text-sm font-helvetica font-medium leading-normal text-center uppercase rounded-full hover:bg-[#7a1f9e] transition-all duration-300 tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkoutLoading === 'Sirloin Cut' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {retryCount > 0 ? `Retrying (${retryCount}/${MAX_RETRIES})...` : 'Processing...'}
                    </span>
                  ) : (
                    'START NOW'
                  )}
                </button>
                {checkoutError && checkoutLoading === 'Sirloin Cut' && (
                  <p className="text-xs text-red-600 mt-2 text-center">{checkoutError}</p>
                )}
              </div>

              {/* Ribeye Cut - Featured */}
              <div className="flex flex-col gap-4 justify-center items-center bg-white rounded-2xl p-6 sm:p-7 lg:p-8 shadow-xl w-full sm:w-[220px] lg:w-[260px] sm:scale-105 lg:scale-110">
                <h4 className="text-[22px] sm:text-[26px] lg:text-[30px] font-bebas font-normal leading-[1.2] text-center text-[#3f1651] uppercase">
                  Ribeye Cut
                </h4>
                <span className="text-[32px] sm:text-[38px] lg:text-[44px] font-bebas font-normal leading-[1.1] text-center text-[#8923b3]">
                  $2,800
                </span>
                <button
                  onClick={() => handleGetStarted('Ribeye Cut')}
                  disabled={checkoutLoading === 'Ribeye Cut'}
                  className="w-full py-3.5 px-6 bg-[#3f1651] text-white text-xs sm:text-sm font-helvetica font-medium leading-normal text-center uppercase rounded-full hover:bg-[#2a0f3a] transition-all duration-300 tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkoutLoading === 'Ribeye Cut' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {retryCount > 0 ? `Retrying (${retryCount}/${MAX_RETRIES})...` : 'Processing...'}
                    </span>
                  ) : (
                    'START NOW'
                  )}
                </button>
                {checkoutError && checkoutLoading === 'Ribeye Cut' && (
                  <p className="text-xs text-red-600 mt-2 text-center">{checkoutError}</p>
                )}
              </div>

              {/* Tomahawk Cut */}
              <div className="flex flex-col gap-4 justify-center items-center bg-white rounded-2xl p-6 sm:p-7 lg:p-8 shadow-xl w-full sm:w-[220px] lg:w-[260px]">
                <h4 className="text-[22px] sm:text-[26px] lg:text-[30px] font-bebas font-normal leading-[1.2] text-center text-[#3f1651] uppercase">
                  Tomahawk Cut
                </h4>
                <span className="text-[32px] sm:text-[38px] lg:text-[44px] font-bebas font-normal leading-[1.1] text-center text-[#8923b3]">
                  $3,900
                </span>
                <button
                  onClick={() => handleGetStarted('Tomahawk Cut')}
                  disabled={checkoutLoading === 'Tomahawk Cut'}
                  className="w-full py-3.5 px-6 bg-[#8923b3] text-white text-xs sm:text-sm font-helvetica font-medium leading-normal text-center uppercase rounded-full hover:bg-[#7a1f9e] transition-all duration-300 tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkoutLoading === 'Tomahawk Cut' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {retryCount > 0 ? `Retrying (${retryCount}/${MAX_RETRIES})...` : 'Processing...'}
                    </span>
                  ) : (
                    'START NOW'
                  )}
                </button>
                {checkoutError && checkoutLoading === 'Tomahawk Cut' && (
                  <p className="text-xs text-red-600 mt-2 text-center">{checkoutError}</p>
                )}
              </div>
            </div>

            {/* Or text and Book a Call Button */}
            <div className="flex flex-col items-center gap-4 mt-10 sm:mt-12">
              <span className="text-[16px] sm:text-[18px] font-poppins font-normal text-white/80">or</span>

              <a
                href="https://calendly.com/john-smulo/purple-cow-unlimited-discovery-call"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  getUserContext().then(context => {
                    const segment = context?.role || 'unauthenticated';
                    trackDiscoveryCallClick(
                      segment === 'super_admin' ? 'super_admin' : 
                      segment === 'ops_admin' || segment === 'admin' ? 'admin' : 
                      segment === 'team' ? 'team' : 
                      segment === 'client' ? 'client' : 'unauthenticated',
                      '/',
                      'faq_bottom'
                    );
                  });
                }}
              >
                <Button
                  text="BOOK A FREE STRATEGY CALL"
                  text_font_size="text-xs sm:text-sm"
                  text_font_family="Helvetica"
                  text_font_weight="font-medium"
                  text_line_height="leading-normal"
                  text_text_align="center"
                  text_text_transform="uppercase"
                  text_color="text-white"
                  fill_background_color="bg-transparent"
                  border_border_radius="rounded-full"
                  className="py-4 px-8 sm:px-10 border-2 border-white hover:bg-white hover:text-[#8923b3] transition-all duration-300 tracking-wider"
                />
              </a>
            </div>

            {/* Subtitle */}
            <p className="text-[14px] sm:text-[16px] lg:text-[18px] font-poppins font-normal leading-[1.6] text-center text-white/90 mt-8 whitespace-nowrap">
              Let's see how Purple Cow Unlimited can become your creative team every month.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}



